import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { BlueprintButton, BlueprintGrid } from "@/components/blueprint/Blueprint";
import { AppScreenshotToggle } from "@/components/shared/AppCapturePlan";
import { SEOHead } from "@/components/shared/SEOHead";
import { APP_STORE_URL } from "@/lib/utils";

export const Route = createFileRoute("/showcase")({
  component: ShowcasePage,
});

const moments: Array<{
  number: string;
  eyebrow: string;
  title: ReactNode;
  body: string;
  tags: string[];
  cta?: { to: string; label: string };
  lightImage?: string;
  darkImage?: string;
  hoverImage?: string;
  showModeToggle?: boolean;
  imageAlt: string;
}> = [
  {
    number: "01",
    eyebrow: "Create",
    title: <>Start With Anything</>,
    body: "Snap a photo while you are out with friends, upload one from your camera roll, describe a direction, or browse the Inkwell for a starting point. The conversation turns a loose moment into a usable creative brief.",
    tags: ["Live Capture", "Photo Upload", "Prompt", "Brief"],
    lightImage: "/clean-screens/createlight.png",
    darkImage: "/clean-screens/createdark.png",
    imageAlt: "Quuil home screen",
  },
  {
    number: "02",
    eyebrow: "Design Tools",
    title: <>Clean Up And Refine</>,
    body: "Turn the moment into merch-ready artwork: remove backgrounds, crop, rotate, scale, apply style effects, compare variations, and keep the versions you want to revisit.",
    tags: ["Background Removal", "Transform", "Variations", "History"],
    cta: { to: "/editor", label: "Explore the Image Editor" },
    lightImage: "/clean-screens/Clean Up And Refine-light.png",
    darkImage: "/clean-screens/Clean Up And Refine-dark.png",
    imageAlt: "Quuil guided design chat screen",
  },
  {
    number: "03",
    eyebrow: "Mockup Editor",
    title: <>Customize The Product</>,
    body: "Put the design on the thing that fits the moment: trip tees, team hats, party stickers, mugs, drinkware, hoodies, and more. Smart print areas keep placement realistic.",
    tags: ["Product Catalog", "Print Areas", "Variants", "Preview"],
    lightImage: "/clean-screens/mockup1.png",
    darkImage: "/clean-screens/mockup1.png",
    hoverImage: "/clean-screens/mockup2.png",
    showModeToggle: false,
    imageAlt: "Quuil product picker screen",
  },
  {
    number: "04",
    eyebrow: "Order + Share",
    title: <>Checkout, Track, And Discover</>,
    body: "Add products to cart, apply coupons, choose shipping, check out with Stripe, track the order, then save, share, or discover new work in the Inkwell.",
    tags: ["Cart", "Stripe", "Tracking", "Inkwell"],
    lightImage: "/clean-screens/checkoutanddiscover.png",
    darkImage: "/clean-screens/checkoutanddiscover.png",
    showModeToggle: false,
    imageAlt: "Quuil design editor screen",
  },
];

function ShowcasePage() {
  return (
    <div className="showcase-page page-enter">
      <SEOHead
        title="Quuil App Showcase"
        description="A look at the Quuil app workflow, from conversational design help and editing to product mockups, secure ordering, and Inkwell discovery."
        path="/showcase"
        keywords={["Quuil app", "merch design workflow", "iPhone design app", "product mockup app"]}
      />

      <section className="showcase-hero">
        <BlueprintGrid />
        <div className="showcase-container showcase-hero-grid">
          <div className="showcase-copy">
            <span className="showcase-kicker">Create // Customize // Order // Share</span>
            <h1>Quuil App Showcase</h1>
            <p>
              A focused look at how QUUIL works as a conversational design partner for real scenarios: capture a moment, shape the artwork, preview products, check out securely, track orders, and discover community work.
            </p>
            <div className="showcase-actions">
              <BlueprintButton href={APP_STORE_URL}>Get the App</BlueprintButton>
              <BlueprintButton href="/gallery" variant="ghost">Enter the Inkwell</BlueprintButton>
            </div>
          </div>
        </div>
      </section>

      <section className="showcase-moments">
        <BlueprintGrid light />
        <div className="showcase-container">
          {moments.map((moment, index) => (
            <article className={`showcase-moment ${index % 2 === 1 ? "is-reverse" : ""}`} key={moment.number}>
              <div className="showcase-moment-copy">
                <span className="showcase-kicker">{moment.number} - {moment.eyebrow}</span>
                <h2>{moment.title}</h2>
                <p>{moment.body}</p>
                <div className="showcase-tags">
                  {moment.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
                {moment.cta ? (
                  <Link to={moment.cta.to} className="showcase-moment-link">
                    {moment.cta.label}
                  </Link>
                ) : null}
              </div>
              <div className="showcase-phone-shot">
                <AppScreenshotToggle
                  title={moment.eyebrow}
                  lightSrc={moment.lightImage}
                  darkSrc={moment.darkImage}
                  alt={moment.imageAlt}
                  priority="screenshot"
                  imageClassName="showcase-screenshot"
                  placeholderClassName="showcase-moment-placeholder"
                  hoverSrc={moment.hoverImage}
                  showModeToggle={moment.showModeToggle ?? true}
                />
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
